<template lang="pug">
.register-row
  .register-group
    .register-key
      .left-side.d-flex
        span.key-name {{ keyName }}
        span.comment(v-if="comment") {{ comment }}
        span.validation-label(v-if="required == true") 必須
      .right-side
        slot(name="question")

    .register-value(v-if="clip")
      FileSelecterClipper(
        v-model="_value"
        :file-name="_fileName"
        :mimetype="mimetype"
        :layout="layout"
        :width="width"
        :height="height"
        @valid-error="onValidError"
      )

    .register-value(v-else)
      FileSelecter(
        v-model="_value"
        :file-name="_fileName"
        :mimetype="mimetype"
        :layout="layout"
        :width="width"
        :height="height"
        :showDragAndDrop="showDragAndDrop"
      )
    
    input(v-model="_value" :name="name" v-validate="validationRules"
      :data-vv-as="(validationKeyName) ? validationKeyName : keyName" style="display: none")

    .error-messages(v-for="error in errors.collect(name)")
      label.error-message {{ error }}
      br

  slot(name="default")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";
import FileSelecter, { FILE_SELECTER_LAYOUT } from "@/pc/basics/FileSelecter.vue";
import FileSelecterClipper from "@/pc/basics/FileSelecterClipper.vue";

/**
 * 入力要素コンポーネント
 *
 * - ファイル選択をサポートする
 * - 既存の入力画面のスタイルが適用される
 * - バリデーターは親コンポーネントのものをInjectionして使用する
 *   - そのため、親コンポーネントでveevalidateをnewしておく必要がある
 */
@Component({
  components: {
    FileSelecter,
    FileSelecterClipper
  }
})
export default class FormFile extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 親コンポーネントでv-modelで指定した値 */
  @Prop({ required: true })
  private value!: any;

  /** 親コンポーネントで指定したファイル名、親コンポーネントとsyncしている */
  @Prop({ required: true })
  private fileName!: string;

  /** 項目名 */
  @Prop({ required: true, type: String })
  private keyName!: string;

  /** 必須か */
  @Prop({
    default: () => {
      return false;
    },
    type: Boolean
  })
  private required!: boolean;

  @Prop({
    default: () => true
  })
  showDragAndDrop!: boolean;

  /** 注釈 */
  @Prop({ type: String })
  private comment!: string;

  /** name属性の値 */
  @Prop({ required: true, type: String })
  private name!: string;

  /** veevalidateに渡すバリデーションルール */
  @Prop()
  private validationRules!: string;

  /** veevalidateに渡すバリデーション項目名 */
  @Prop({
    default: () => {
      return "";
    },
    type: String
  })
  private validationKeyName!: string;

  /** コンポーネント横幅 */
  @Prop({
    default: () => {
      return "600px";
    },
    type: String
  })
  private width!: string;

  /** コンポーネント縦幅 */
  @Prop({
    default: () => {
      return "150px";
    },
    type: String
  })
  private height!: string;

  /**
   * レイアウト
   * vertical or horizontal
   */
  @Prop({
    default: () => {
      return FILE_SELECTER_LAYOUT[0];
    },
    validator: (layout) => {
      return FILE_SELECTER_LAYOUT.indexOf(layout) !== -1;
    },
    type: String
  })
  private layout!: string;

  /** ファイルのmimeType */
  @Prop({
    required: true,
    type: String
  })
  private mimetype!: string;

  /** トリミング可否 */
  @Prop({
    default: () => {
      return false;
    },
    type: Boolean
  })
  private clip!: boolean;

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _value(): { file: File | string; fileName: string } {
    return this.value;
  }

  /** 値が変更されたら親コンポーネントに値を渡す */
  set _value(file: { file: File | string; fileName: string }) {
    // ファイル選択は更新されたタイミングでバリデーションする
    this.inputValue(file.file, file.fileName);

    return;
  }

  /**
   * 選択されたファイルを親コンポーネントに返す
   */
  private async inputValue(file: File | string, fileName: string): Promise<void> {
    // ファイルが存在した場合はバリデーションチェックして親コンポーネントにファイルを渡すか判断する
    // ファイルが存在しない場合はそのまま空のファイルを親コンポーネントに渡す
    if (file) {
      const validationResult: boolean = await this.validation(file);

      // エラーでなければ親コンポーネントに選択されたファイルを渡す
      // エラーなら空ファイルを渡す
      if (validationResult) {
        this.$emit("input", file);
        this.$emit("update:file-name", fileName);
      } else {
        this.$emit("input", null);
        this.$emit("update:file-name", "");
      }
    } else {
      this.$emit("input", null);
      this.$emit("update:file-name", "");
    }

    return;
  }

  /**
   * バリデーション
   */
  private async validation(file: File | string): Promise<boolean> {
    // 現在のバリデーションエラーをリセットしてから、バリデーションを実施する
    this.$validator.errors.remove(this.name);

    const keyName: string = this.validationKeyName ? this.validationKeyName : this.keyName;
    const { valid, errors } = await this.$validator.verify(file, this.validationRules, { name: keyName, bails: false });

    // エラーの場合はエラーメッセージを表示する
    if (!valid) {
      for (const error of errors) {
        this.$validator.errors.add({ field: this.name, msg: error });
      }
    }

    return valid;
  }

  /**
   * ファイル入力コンポーネントでのエラー
   */
  private onValidError(): void {
    // 現在のバリデーションエラーをリセットしてから、バリデーションメッセージを格納する
    this.$validator.errors.remove(this.name);
    this.$validator.errors.add({ field: this.name, msg: "画像ファイル以外は指定できません" });

    return;
  }

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _fileName(): string {
    return this.fileName;
  }

  set _fileName(value) {
    return;
  }
}
</script>
<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
.comment {
  color: $validation-label-font-color;
}

.right-side {
  margin-left: auto;
}
</style>
